import React, { useMemo, useState } from "react";
import { Box, Button, Typography, Alert, CircularProgress } from "@mui/material";
import axios from "axios";
import supabase from "../supabaseClient";

export default function AdminQueues() {
  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const [reindexing, setReindexing] = useState(false);
  const [reindexMessage, setReindexMessage] = useState("");
  const [reindexError, setReindexError] = useState("");

  const bullBoardUrl = useMemo(() => {
    if (!SERVER_URL) return null;
    // Bull Board is served by the server (NOT the client) at /admin/queues
    return `${SERVER_URL}/admin/queues`;
  }, [SERVER_URL]);

  const triggerReindex = async () => {
    if (!SERVER_URL) return;
    const ok = window.confirm(
      "Trigger a full search reindex?\n\nThis recreates the Elasticsearch index and reindexes all recipes. It may take a while."
    );
    if (!ok) return;

    setReindexError("");
    setReindexMessage("");
    setReindexing(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      await axios.post(
        `${SERVER_URL}/admin/api/system/reindex`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReindexMessage("Reindex started in background. Check server logs for progress.");
    } catch (e) {
      setReindexError(e?.response?.data?.error || e?.message || "Failed to trigger reindex");
    } finally {
      setReindexing(false);
    }
  };

  if (!bullBoardUrl) {
    return (
      <Box sx={{ mt: 3 }}>
        <Alert severity="error">
          Missing `REACT_APP_SERVER_URL` — cannot link to Bull Board.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h5" gutterBottom>
        queues
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.8, mb: 2 }}>
        This is Bull Board for BullMQ. Use it to pause/resume queues and remove/retry
        jobs. (Active-job cancellation is handled via the “cancel” button on the admin
        upload page.)
      </Typography>

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
        <Button
          variant="contained"
          onClick={() => window.open(bullBoardUrl, "_blank", "noopener,noreferrer")}
        >
          open in new tab
        </Button>
        <Button variant="outlined" href={bullBoardUrl} target="_blank" rel="noreferrer">
          open (link)
        </Button>
        <Button
          variant="outlined"
          onClick={triggerReindex}
          disabled={reindexing}
        >
          {reindexing ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} />
              reindexing…
            </Box>
          ) : (
            "trigger reindex"
          )}
        </Button>
      </Box>

      {reindexMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {reindexMessage}
        </Alert>
      )}
      {reindexError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {reindexError}
        </Alert>
      )}

      <Box
        sx={{
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 2,
          overflow: "hidden",
          height: { xs: "70vh", md: "78vh" },
        }}
      >
        <iframe
          title="Bull Board"
          src={bullBoardUrl}
          style={{ width: "100%", height: "100%", border: "0" }}
        />
      </Box>
    </Box>
  );
}


